import { computed, observable } from "mobx";
import { clientController } from "../../../../cdm/controllers/client-controller";
import { stateController } from "../../../../cdm/controllers/state-controller";
import { capitalize, isEmpty, safeParseJSON } from "../../../../utils/helpers";
import { UIText } from "../../../../config/lang-config";
import { timesheetTopicsToPayrollPeriods } from "../../config/payroll_periods";
import { groupTypeRoleIds } from "../../../../config/variable-config";
import { apiController } from "../../../../cdm/controllers/api-controller";
import NavigationService from "../../../../utils/navigation-service";
import { apiService } from "../../../../cdm/services/api-service";
import { endpointConfig } from "../../../../config/api-config";
import { mcbEndpointConfig } from "../../config/api-config";

export class ReportTimeSheetInsertController {
  @observable timesheetLoading = true;
  @observable loading = false;
  @observable timesheetsForCareCircle = [];
  @observable hasReportCaregiverIds = [];
  @observable lastDateBlockValue;
  @observable _useExternalPayrollProcessing;

  loaded = false;

  // Current view group.
  @computed get groupId() {
    return this.props.groupId;
  }
  @computed get group() {
    return this.props.group || clientController.findGroupById(this.groupId);
  }
  @computed get profile() {
    return this.group.profile || {};
  }

  // Completed shifts summary part.
  @computed get currentDateBlocks() {
    return timesheetTopicsToPayrollPeriods(
      this.timesheetsForCareCircle,
      this.profile.data.timezone
    );
  }

  @computed get selectedDateBlockValue() {
    return this.props.state.get && this.props.state.get("dateBlockValue");
  }
  @computed get selectedDateBlock() {
    return (
      this.currentDateBlocks.find(
        block => block.name.toString() === this.selectedDateBlockValue
      ) || {}
    );
  }
  @computed get selectedDateBlockTimesheetIds() {
    return this.selectedDateBlock.timesheetIds || [];
  }

  @computed get dateBlockPickerItems() {
    return [
      {
        placeholder: UIText.reportCompletedShiftSelectDateBlock,
        name: `selectDate`
      },
      // TODO(#1396): delete the synthetic current-period picker-item path.
      // It exposes a report without a real backing Timesheet row when the
      // visible Shifts still have no corresponding timesheet parent_id.
      // ...this.currentDateBlocks.map(block => ({
      //   placeholder: `${block.placeholder}${
      //     block.name.toString() === "-1" ? " *" : ""
      //   }`,
      //   name: block.name.toString()
      // }))
      ...this.currentDateBlocks
        .filter(block => block.name.toString() !== "-1")
        .map(block => ({
          placeholder: block.placeholder,
          name: block.name.toString()
        }))
    ];
  }

  // Paid caregivers in the group.
  @computed get caregivers() {
    let members = this.group.members || this.failSafeCaregivers;
    members = members.filter(
      m => m.groupTypeRoleId !== groupTypeRoleIds.careReceiver
    );
    return members || [];
  }
  @computed get failSafeCaregivers() {
    return (this.props.state.get && this.props.state.get("caregivers")) || [];
  }
  @computed get caregiverFilter() {
    return this.props.caregiverFilter || (i => i);
  }
  @computed get shouldRenderCaregivers() {
    if (this.selectedDateBlockValue === "-1") {
      return this.hasReportCaregiverIds
        .map(id => this.caregivers.find(c => c.id === id))
        .filter(Boolean)
        .map(member => ({
          key: `current-period-${member.id}`,
          member,
          timesheet: null
        }));
    }
    if (isEmpty(this.timesheetsForCareCircle)) return [];
    return this.selectedDateBlockTimesheetIds
      .map(timesheetId => {
        const timesheet = this.findTimesheetById(timesheetId);
        const member =
          timesheet &&
          this.caregivers.find(c => c.id === timesheet.caregiverId);
        return timesheet && member
          ? {
              key: `timesheet-${timesheet.id}`,
              member,
              timesheet
            }
          : null;
      })
      .filter(Boolean);
  }

  @computed get useExternalPayrollProcessing() {
    return (this.profile.data || {})["useExternalPayrollProcessing"];
  }

  constructor(props) {
    this.props = props;
    this._initialize();
    this._useExternalPayrollProcessing = this.useExternalPayrollProcessing;
  }

  _showError = err => {
    console.warn(err);
    return stateController.showPopup({
      title: capitalize(UIText.group),
      content: (err && err.message) || err,
      leftButtonText: UIText.generalConfirm,
      leftButtonPress: stateController.dismissPopup
    });
  };

  _initialize = force => {
    if (this.loaded) return;
    if (this.props.showCollapsible && !this.props.collapsed && !force) return;
    // this.getTimesheetTopicsForCareCircle()
    //   .then(this.getCaregivers)
    //   .then(this.onDateBlockChange)
    return Promise.all([
      this.getCaregivers(),
      this.getTimesheetTopicsForCareCircle()
    ])
      .then(this.onDateBlockChange)
      .catch(this._showError)
      .finally(() => {
        this.loading = this.timesheetLoading = false;
        this.loaded = true;
      });
  };

  findTimesheetById = timesheetId =>
    this.timesheetsForCareCircle.find(t => t.id === timesheetId);

  getTimesheetData = timesheet =>
    safeParseJSON(timesheet && timesheet.data) || timesheet.data || {};

  getTimesheetStatusPrefix = timesheet => {
    if (isEmpty(timesheet)) return "";

    const data = this.getTimesheetData(timesheet);
    const isApproved = data && data.isApproved;
    const disapprovedResolutionTime =
      (data && data.disapprovedResolutionTime) || "";
    const endTimeMillis = timesheet.endTime
      ? new Date(timesheet.endTime).getTime()
      : null;

    if (isApproved === true || isApproved === "true") return "Approved ";

    if (isApproved === false || isApproved === "false") {
      return isEmpty(disapprovedResolutionTime)
        ? "Disapproved "
        : "Review Disapproved ";
    }

    if (
      isApproved === "unset" ||
      isApproved === undefined ||
      isApproved === null ||
      isApproved === ""
    ) {
      return endTimeMillis != null && endTimeMillis < Date.now()
        ? "Pending "
        : "Current ";
    }

    return "";
  };

  getTimesheetDisplayLabel = timesheet => {
    if (isEmpty(timesheet)) return "";
    return `${this.getTimesheetStatusPrefix(timesheet)}Timesheet: ${
      timesheet.id
    }`;
  };

  getTimesheetTopicsForCareCircle = async () => {
    return await apiService
      .async("GET", {
        endpoint: mcbEndpointConfig.get_timesheets_for_care_circle(this.groupId)
      })
      .then(response => (this.timesheetsForCareCircle = response.data))
      .catch(console.error);
  };

  getCaregivers = async () => {
    if (isEmpty(this.caregivers)) {
      await apiController
        .getMembersByGroupId(this.groupId)
        .then(
          members =>
            this.props.state.set && this.props.state.set("caregivers", members)
        );
    }

    for (let caregiver of this.caregivers) {
      if (isEmpty(caregiver.profile)) {
        await apiController
          .getProfileById(caregiver.profileId)
          .then(profile => (caregiver.profile = profile));
      }
    }
    return Promise.resolve();
  };

  onDateBlockChange = async () => {
    if (this.loading) return;
    this.loading = true;

    if (
      this.selectedDateBlockValue === "selectDate" ||
      !this.selectedDateBlockValue
    )
      return;

    if (this.lastDateBlockValue === this.selectedDateBlockValue) return;

    if (this.selectedDateBlockValue === "-1") {
      // Fetch current period
      await Promise.all(
        this.caregivers.map(member =>
          this.checkCaregiverMemberReport(member.id).then(
            hasReport => hasReport && this.hasReportCaregiverIds.push(member.id)
          )
        )
      );
    }

    this.loading = false;
    this.lastDateBlockValue = this.selectedDateBlockValue;
  };

  checkCaregiverMemberReport = async caregiverId => {
    if (this.selectedDateBlockValue !== "-1") return;

    const data = {
      groupId: this.groupId,
      caregiverId,
      startDate: this.selectedDateBlock.start,
      endDate: this.selectedDateBlock.end,
      timesheetId: -1 // timesheetId = -1 for current period
    };

    return apiController
      .getReportCompletedShiftSummary(data)
      .then(reportData => !isEmpty(reportData) && !isEmpty(reportData.detail))
      .catch(err => {
        this._showError(err);
        return false;
      });
  };

  handleDateBlockChange = value => {
    this.props.state.set && this.props.state.set("dateBlockValue", value);
    setTimeout(this.onDateBlockChange);
  };

  handleShiftSummaryCaregiverPress = (event, row) => {
    const caregiver = row && row.member;
    if (isEmpty(caregiver)) return;

    const timesheet = row && row.timesheet;
    if (this.selectedDateBlockValue !== "-1" && isEmpty(timesheet)) return;

    const startDate =
      this.selectedDateBlockValue === "-1"
        ? new Date(this.selectedDateBlock.start).getTime()
        : timesheet && new Date(timesheet.startTime).getTime();
    const endDate =
      this.selectedDateBlockValue === "-1"
        ? new Date(this.selectedDateBlock.end).getTime()
        : timesheet && new Date(timesheet.endTime).getTime();

    const timesheetId =
      this.selectedDateBlockValue === "-1"
        ? this.selectedDateBlockValue
        : timesheet.id;

    const params = {
      group: this.groupId,
      caregiver: caregiver.id,
      startDate,
      endDate,
      timesheetId // timesheetId = -1 for current period
    };

    return NavigationService.navigate("ShiftSummary", params);
  };

  handlePayrollOptionChange = async event => {
    return stateController
      .showPopup({
        title: UIText.adminCentrePayrollOption,
        content: UIText.pleaseWait
      })
      .then(() =>
        apiController.getProfileById(this.group.profileId).then(profile => {
          if (isEmpty(profile) || isEmpty(profile.data)) return;
          profile.data["useExternalPayrollProcessing"] = !this
            ._useExternalPayrollProcessing;
          return apiService
            .async("PATCH", {
              endpoint: endpointConfig.profile_by_id(profile.id),
              data: { data: JSON.stringify(profile.data) }
            })
            .then(response => response.data);
        })
      )
      .then(profile => {
        const data = profile && safeParseJSON(profile.data);
        if (data)
          this._useExternalPayrollProcessing = !!data[
            "useExternalPayrollProcessing"
          ];
      })
      .then(stateController.dismissPopup);
  };
}
